import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  DollarSign, 
  FileText, 
  Download, 
  Target, 
  TrendingUp, 
  Clock,
  Info,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Emenda } from '../admin/types/emenda';

export const AmendmentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [amendment, setAmendment] = useState<Emenda | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchAmendment(id);
    }
  }, [id]);

  const fetchAmendment = async (amendmentId: string) => {
    try {
      const { data, error } = await supabase
        .from('emendas')
        .select(`
          *,
          documentos:emenda_documentos(*)
        `)
        .eq('id', amendmentId)
        .single();
      
      if (error) throw error;
      setAmendment(data);
    } catch (err) {
      console.error('Error fetching amendment:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Carregando detalhes...</p>
      </div>
    );
  }

  if (!amendment) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-screen">
        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
           <Info size={40} />
        </div>
        <h2 className="text-3xl font-black text-primary mb-4">Emenda não encontrada</h2>
        <button 
          onClick={() => navigate('/emendas')}
          className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg active:scale-95 flex items-center gap-2 mx-auto"
        >
          <ArrowLeft size={18} />
          Voltar para a lista
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-6 py-12 min-h-screen pb-32">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-10">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <ChevronRight size={12} />
        <Link to="/emendas" className="hover:text-blue-600">Emendas</Link>
        <ChevronRight size={12} />
        <span className="text-slate-900 truncate max-w-[200px]">{amendment.titulo}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3">
              <span className="px-4 py-1.5 bg-blue-600 text-white text-[10px] font-black rounded-lg uppercase tracking-widest shadow-lg shadow-blue-500/20">
                {amendment.area_investimento}
              </span>
              <span className="text-slate-400 font-bold text-xs uppercase tracking-widest border-l border-slate-200 pl-3">
                Ano {new Date(amendment.data_publicacao).getFullYear()}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-primary leading-tight tracking-tight">
              {amendment.titulo}
            </h1>

            {amendment.imagem_url && (
              <div className="w-full aspect-video rounded-[3rem] overflow-hidden shadow-2xl shadow-blue-900/10 border border-slate-100">
                <img src={amendment.imagem_url} alt={amendment.titulo} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="bg-slate-50/50 p-8 rounded-[2.5rem] border border-slate-100">
               <p className="text-xl text-slate-700 leading-relaxed font-semibold italic">
                "{amendment.resumo}"
              </p>
            </div>
          </motion.div>

          <section className="space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-sm">
                <Info size={24} />
              </div>
              <h2 className="text-3xl font-black text-primary tracking-tight">Descrição do Projeto</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed font-medium text-lg whitespace-pre-wrap">
              {amendment.descricao_completa}
            </div>
          </section>

          {/* Documents Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center shadow-sm">
                <FileText size={24} />
              </div>
              <h2 className="text-3xl font-black text-primary tracking-tight">Documentos Transparência</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {amendment.documentos && amendment.documentos.length > 0 ? (
                amendment.documentos.map((doc, i) => (
                  <a
                    key={doc.id}
                    href={doc.arquivo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-6 bg-white border border-slate-200 rounded-[2rem] hover:border-blue-600 hover:shadow-xl hover:-translate-y-1 transition-all group text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center group-hover:bg-red-500 group-hover:text-white transition-all transform group-hover:scale-110">
                        <FileText size={20} />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">DOWNLOAD PDF</span>
                        <span className="font-black text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1">
                          {doc.nome}
                        </span>
                      </div>
                    </div>
                    <Download size={20} className="text-slate-300 group-hover:text-blue-600 group-hover:translate-y-1 transition-all" />
                  </a>
                ))
              ) : (
                <div className="col-span-full p-12 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200 text-center">
                  <p className="text-slate-400 font-black text-xs uppercase tracking-widest">Nenhum documento anexado ainda.</p>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-8">
          <div className="bg-slate-900 p-10 rounded-[3.5rem] text-white shadow-2xl shadow-blue-900/20 space-y-10 relative overflow-hidden group">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-600/20 rounded-full blur-[100px] group-hover:bg-blue-600/30 transition-colors duration-700"></div>
            
            <div className="relative z-10">
              <p className="text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4">Valor do Investimento</p>
              <p className="text-5xl font-black tracking-tighter">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amendment.valor)}
              </p>
            </div>

            <div className="space-y-8 relative z-10">
              <div className="flex items-start gap-4 p-4 rounded-3xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <ExternalLink size={20} />
                </div>
                <div>
                  <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Origem da Verba</p>
                  <p className="font-bold text-slate-100">{amendment.origem_verba}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-3xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Localização</p>
                  <p className="font-bold text-slate-100">{amendment.municipio} - {amendment.estado}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-3xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <Calendar size={20} />
                </div>
                <div>
                  <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Publicação</p>
                  <p className="font-bold text-slate-100">
                    {new Date(amendment.data_publicacao).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-10 rounded-[3.5rem] shadow-sm flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-[1.5rem] flex items-center justify-center mb-6">
               <Target size={32} />
            </div>
            <h4 className="text-xl font-black text-slate-900 mb-4 tracking-tight">Ficou com alguma dúvida?</h4>
            <p className="text-sm font-medium text-slate-500 mb-8 leading-relaxed">
              Para obter mais informações técnicas ou orçamentárias sobre este projeto, entre em contato com nossa equipe.
            </p>
            <Link 
              to="/contato"
              className="w-full bg-slate-50 text-slate-900 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-900 hover:text-white transition-all active:scale-95 shadow-sm"
            >
              Falar agora
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
